import { useState, useEffect, useCallback } from 'react'
import { AlertTriangle, X } from 'lucide-react'
import { RATE_LIMIT_EVENT } from '../../utils/api'
import type { RateLimitDetail } from '../../utils/api'
import styles from './RateLimitBanner.module.css'

/**
 * Global banner that appears when the user hits API usage limits (429).
 * Listens for custom events dispatched by the API response interceptor.
 * Auto-dismisses after 15 seconds or can be closed manually.
 */
export const RateLimitBanner = () => {
  const [visible, setVisible] = useState(false)
  const [detail, setDetail] = useState<RateLimitDetail | null>(null)

  const handleRateLimit = useCallback((e: Event) => {
    const { detail: d } = e as CustomEvent<RateLimitDetail>
    setDetail(d)
    setVisible(true)
  }, [])

  useEffect(() => {
    window.addEventListener(RATE_LIMIT_EVENT, handleRateLimit)
    return () => window.removeEventListener(RATE_LIMIT_EVENT, handleRateLimit)
  }, [handleRateLimit])

  // Auto-dismiss after 15 seconds
  useEffect(() => {
    if (!visible) return
    const timer = setTimeout(() => setVisible(false), 15_000)
    return () => clearTimeout(timer)
  }, [visible])

  if (!visible || !detail) return null

  const limitInfo = detail.limit
  const resetTime = limitInfo?.resetsAt
    ? new Date(limitInfo.resetsAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : null

  return (
    <div className={styles.banner} role="alert">
      <div className={styles.bannerContent}>
        <AlertTriangle size={18} className={styles.icon} />
        <div className={styles.text}>
          <strong>API Limit Reached</strong>
          <span>{detail.message}</span>
          {limitInfo && (
            <span className={styles.limitDetail}>
              {limitInfo.current.toLocaleString()} / {limitInfo.max.toLocaleString()} calls used
              {resetTime && <> &middot; Resets at {resetTime}</>}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={() => setVisible(false)}
          className={styles.dismiss}
          aria-label="Dismiss"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  )
}
